import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { IMetricValue, IQueryFilter } from '../type'

export interface IMetricData {
  [metricCode: string]: IMetricValue
}

export interface ILocationData {
  locationId: number
  startDate: string
  endDate: string
  metricData: IMetricData | null
}

const gqlQuery = gql`
  query ListLocationMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    listLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        startDate
        endDate
        locationId
        metricData
        metricSummaryData
      }
    }
  }
`

function useListLocationMetricValues(
  startDate: string,
  endDate: string,
  queryFilter: IQueryFilter,
  metricCodes: string[],
) {
  const { data, loading } = useQuery(gqlQuery, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_ids: queryFilter?.locationIds,
        location_group_ids: queryFilter?.locationGroupIds,
        intersected_location_group_ids:
          queryFilter?.intersectedLocationGroupIds,
        metrics: metricCodes,
      },
    },
    skip: !startDate || !endDate || !queryFilter || !metricCodes,
  })

  return {
    isLoading: loading,
    data: useMemo(() => {
      if (!data?.listLocationMetricValues?.nodes) {
        return null
      }

      const locations: ILocationData[] = []
      const summary: IMetricData = {}

      data.listLocationMetricValues.nodes.forEach((node: any) => {
        const {
          startDate,
          endDate,
          locationId,
          metricData,
          metricSummaryData,
        } = node

        const formattedMetricData: IMetricData = {}
        if (metricData) {
          Object.keys(metricData).forEach((metricCode: string) => {
            const value = metricData[metricCode]
            if (value) {
              formattedMetricData[metricCode] = {
                code: metricCode,
                name: value.name,
                unit: value.unit,
                value: value.value,
              }
            }
          })
        }

        // Add to locationData
        locations.push({
          locationId,
          startDate,
          endDate,
          metricData: formattedMetricData,
        })

        // Add to summaryData if it exists
        if (metricSummaryData) {
          Object.keys(metricSummaryData).forEach((metricCode: string) => {
            const value = metricSummaryData[metricCode]
            if (value) {
              summary[metricCode] = {
                code: metricCode,
                name: value.name,
                unit: value.unit,
                value: value.value,
              }
            }
          })
        }
      })

      return {
        locations,
        summary,
      }
    }, [data, startDate, endDate, queryFilter, metricCodes]),
  }
}

export default useListLocationMetricValues
